/**
 * CreateDashboardDialog — modal for spawning a new dashboard.
 *
 * Two paths:
 *   - "Blank" — creates an empty user-owned dashboard, navigates to
 *     the new id in edit mode.
 *   - "Clone from pack" — picks a pack-shipped dashboard, clones it,
 *     navigates to the clone in edit mode.
 *
 * Phase 0 calls `dashboards.create()` / `dashboards.clone()` (in-memory +
 * localStorage). Phase 1 swaps to `POST /v1/iot/dashboards` (already
 * shipped server-side — see Arun's M3).
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Layers, Sparkles } from "lucide-react";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Badge } from "@/lib/ui/badge";
import { cn } from "@/lib/ui/cn";
import { useDashboards } from "@/lib/dashboard/store";
import { useToast } from "@/lib/ui/toast";

export interface CreateDashboardDialogProps {
  open: boolean;
  onClose: () => void;
}

type TemplateChoice =
  | { kind: "blank" }
  | { kind: "clone"; sourceId: string };

export function CreateDashboardDialog({ open, onClose }: CreateDashboardDialogProps) {
  const dashboards = useDashboards();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [template, setTemplate] = useState<TemplateChoice>({ kind: "blank" });

  // Templates available: blank + every pack-shipped dashboard.
  const packDashboards = useMemo(
    () => dashboards.dashboards.filter((d) => d.source === "pack"),
    [dashboards.dashboards],
  );

  const handleClose = () => {
    setName("");
    setDescription("");
    setTemplate({ kind: "blank" });
    onClose();
  };

  const canCreate = name.trim().length > 1;

  const handleCreate = () => {
    if (!canCreate) return;
    let newId: string;
    if (template.kind === "blank") {
      newId = dashboards.create({
        name: name.trim(),
        description: description.trim() || undefined,
        widgets: [],
      });
    } else {
      newId = dashboards.clone(template.sourceId, name.trim());
      if (description.trim()) {
        dashboards.update(newId, { description: description.trim() });
      }
    }
    toast.push("ok", `Created dashboard "${name.trim()}"`);
    handleClose();
    navigate(`/app/dashboards/${newId}?edit=1`);
  };

  return (
    <Sheet open={open} onClose={handleClose} widthClass="max-w-lg" ariaLabel="Create dashboard">
      <SheetHeader
        title="New dashboard"
        subtitle="Start blank or clone a pack-shipped template."
        onClose={handleClose}
      />

      <div className="flex flex-1 flex-col gap-4 p-5">
        <LabelledField label="Dashboard name">
          <Input
            autoFocus
            data-tour="dashboard-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Melt shop overview"
          />
        </LabelledField>
        <LabelledField label="Description (optional)">
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this dashboard is for"
          />
        </LabelledField>

        <div className="flex flex-col gap-1" data-tour="dashboard-template">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
            Template
          </span>
          <div className="flex flex-col gap-2">
            <TemplateOption
              active={template.kind === "blank"}
              onClick={() => setTemplate({ kind: "blank" })}
              icon={Sparkles}
              title="Blank"
              subtitle="An empty grid. Drag widgets in from the editor palette."
            />
            {packDashboards.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                  Clone from pack
                </span>
                <div className="flex flex-col gap-1.5">
                  {packDashboards.map((p) => (
                    <TemplateOption
                      key={p.id}
                      active={
                        template.kind === "clone" && template.sourceId === p.id
                      }
                      onClick={() => setTemplate({ kind: "clone", sourceId: p.id })}
                      icon={Layers}
                      title={`Clone "${p.name}"`}
                      subtitle={`${p.widgets.length} widget${p.widgets.length === 1 ? "" : "s"} · ${p.pack ?? "pack"} pack`}
                      meta={
                        <Badge variant="info" className="text-[9px]">
                          {p.pack ?? "pack"}
                        </Badge>
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between border-t border-border pt-4">
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button size="sm" data-tour="dashboard-create" onClick={handleCreate} disabled={!canCreate}>
            Create
            <ArrowRight className="ml-1.5 size-4" />
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

function TemplateOption({
  active,
  onClick,
  icon: Icon,
  title,
  subtitle,
  meta,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Sparkles;
  title: string;
  subtitle: string;
  meta?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-md border p-3 text-left transition-colors",
        active
          ? "border-brand bg-brand-muted/30"
          : "border-border bg-surface hover:border-brand/60",
      )}
    >
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand">
        <Icon className="size-3.5" />
      </div>
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-on-surface">{title}</span>
          {active ? (
            <Badge variant="default" className="text-[9px]">
              <Check className="mr-1 size-3" />
              Selected
            </Badge>
          ) : (
            meta
          )}
        </div>
        <span className="text-[11px] text-on-surface-muted">{subtitle}</span>
      </div>
    </button>
  );
}

function LabelledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
